import * as yup from 'yup'
import styled from '@emotion/styled'
import { useSnackbar } from 'notistack'
import { useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PersonIcon from '@mui/icons-material/Person'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Button, Grid, Typography } from '@mui/material'
import { useForm, FormProvider, useFormContext } from 'react-hook-form'

import { useMeta } from '@contexts/Meta'
import keycloak from '../../../keycloak'
import useAPIFetch from '@hooks/useAPIFetch'
import { AuthContext } from '@contexts/Auth'
import { UserInfo } from '@models/user.model'
import useLocalStorage from '@hooks/useLocalStorage'
import { dirtyValues } from '@utils/filterDirtyFields'
import UserService from '@services/users/user.service'
import AuthService from '@services/users/auth.service'
import Header from '@components/configurations/header'
import { LanguageSetting } from './LanguageSeetingForm'
import { PasswordSettingForm } from './PasswordSettingForm'
import { FormTextField } from '@components/configurations/form/textfield'
import ValidationToast from '@components/configurations/form/ValidationToast'
import { CompactFieldRow } from '@components/configurations/form/compactFieldRow'
import UserInfoForm from '@components/configurations/users_config/users/form/userInfoForm'

const FormBox = styled(Box)(() => ({
	display: 'flex',
	flexDirection: 'column',
	gap: '12px',
}))

interface ProfileSettingForm {
	id?: string
	first_name?: string
	last_name?: string
	company?: string
	position?: string
	phone?: string
	email?: string
	current_password?: string
	new_password?: string
	confirm_password?: string
}

export const ProfileSetting = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const [, { setMeta }] = useMeta()
	const { enqueueSnackbar } = useSnackbar()
	const { setRequest, isLoading } = useAPIFetch()
	const { state, dispatch } = useContext(AuthContext)

	const validationSchema = yup
		.object()
		.shape({
			first_name: yup.string().max(255).trim().required('First Name is required'),
			last_name: yup.string().max(255).trim().required('Last Name is required'),
			company: yup.string().nullable().max(255),
			position: yup.string().nullable().max(255),
			phone: yup.string().nullable().max(50),
		})
		.required()

	const methods = useForm({
		// defaultValues: {
		// 	...state.userInfo,
		// 	current_password: '',
		// 	new_password: '',
		// 	confirm_password: '',
		// },
		resolver: yupResolver(validationSchema),
		mode: 'all',
		reValidateMode: 'onChange',
		criteriaMode: 'all',
	})

	const { reset } = methods

	useEffect(() => {
		reset({ ...state.userInfo })
	}, [state.userInfo])

	const {
		handleSubmit,
		control,
		formState: { isDirty, dirtyFields },
	} = methods

	useEffect(() => {
		setMeta({ isLoading })
	}, [isLoading])

	const onSubmit = (formVal: any) => {
		const data = formVal as ProfileSettingForm
		let new_value: any = dirtyValues(dirtyFields, data)
		let changedValueKey = Object.keys(new_value)
		console.log('changedValueKey', changedValueKey)
		if (!isDirty) {
			enqueueSnackbar('You have no update in this session.', { variant: 'warning' })
		} else {
			console.log('new_value', new_value)
			if (
				changedValueKey.length === 3 &&
				changedValueKey.includes('confirm_password') &&
				changedValueKey.includes('current_password') &&
				changedValueKey.includes('new_password')
			) {
				//only change password
				changePassword(data)
			} else {
				setRequest({
					callback: async (cancelToken: any) => {
						console.log('udpate user', new_value)
						await UserService.UpdateUserProfile(new_value, data?.id ?? '').then(async (rep) => {
							dispatch({ type: 'updateProfile', userInfo: data as UserInfo })
							enqueueSnackbar('User profile updated.', { variant: 'success' })
							changePassword(data)
						})
					},
				})
			}
		}
	}

	const changePassword = (data: ProfileSettingForm) => {
		console.log('changepasswword', data)
		if (
			(data.current_password === data.new_password ||
				data.current_password === data.confirm_password) &&
			data.current_password !== '' &&
			data.new_password !== '' &&
			data.confirm_password !== ''
		) {
			enqueueSnackbar('Please make sure the new password is different from current password', {
				variant: 'warning',
			})
		} else if (data.confirm_password !== data.new_password) {
			enqueueSnackbar('Please make sure the new password is same with confirm password', {
				variant: 'warning',
			})
		} else if (data.current_password && data.new_password && data.confirm_password) {
			setRequest({
				callback: async (cancelToken: any) => {
					await AuthService.ChangePassword({
						current_password: data.current_password ?? '',
						new_password: data.new_password ?? '',
						confirm_password: data.confirm_password ?? '',
					}).then(async (rep) => {
						enqueueSnackbar('Password changed. Pleases login again.', { variant: 'success' })
						dispatch({ type: 'changePassword' })
						navigate('/')

						keycloak.logout({ redirectUri: window.location.origin })
					})
				},
			})
		}
	}

	return (
		<Grid minHeight={'100vh - 80px'} component="form" onSubmit={handleSubmit(onSubmit)}>
			<Header
				title={t('User Profile')}
				icon={<PersonIcon sx={{ fontSize: 40, color: '#707070' }} />}
			/>
			<FormProvider {...methods}>
				<FormBox>
					<CompactFieldRow
						firstchildren={
							<FormTextField name="first_name" control={control} label={t('First Name')} />
						}
						secondchildren={
							<FormTextField name="last_name" control={control} label={t('Last Name')} />
						}
					/>
					<CompactFieldRow
						firstchildren={
							<FormTextField name="company" control={control} label={t('Organisation')} />
						}
						secondchildren={
							<FormTextField name="position" control={control} label={t('Dept/Team')} />
						}
					/>
					<CompactFieldRow
						firstchildren={<FormTextField name="phone" control={control} label={t('Phone')} />}
						secondchildren={
							<FormTextField name="email" control={control} label={t('E-mail')} uneditable={true} />
						}
					/>
					{/* <CompactFieldRow
                firstchildren={<FormSwitch name="active" control={control} label={t("Active")}/>}
                secondchildren={<></>}
                /> */}
				</FormBox>

				<PasswordSettingForm />

				{/* <LanguageSetting /> */}

				<Box
					sx={{
						display: 'flex',
						justifyContent: 'flex-end',
						gap: '12px',
						marginTop: '12px',
						bottom: 30,
						right: 30,
						position: 'absolute',
					}}
				>
					<Button variant="contained" color="defaultButton" type="submit">
						{t('Save')}
					</Button>
					<Button
						variant="contained"
						color="error"
						onClick={() => navigate('/configurations/users_config')}
					>
						{t('Cancel')}
					</Button>
				</Box>
				<ValidationToast control={control} />
			</FormProvider>
		</Grid>
	)
}
